import React, { useEffect, useState } from "react";
import {
  Dimensions,
  Modal,
  Pressable,
  ScrollView,
  Text,
  TextInput,
} from "react-native";
import { View } from "ui/atoms/view";
import { CheckIcon, CloseIcon, EditIcon, Icon } from "ui/gluestack/icon";
import { PortalHost } from "ui/modules/ThrelsPortal/PortalHost";

export interface ReusableModalProps {
  isVisible: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  hasCancelButton?: boolean;
  noBackgroundClose?: boolean;
  isEditing?: boolean;
  canEditTitle?: boolean;
  fullScreen?: boolean;
  blur?: boolean;
  onTitleUpdate?: (newTitle: string) => void;
  footer?: React.ReactNode;
}

export function ReusableModal({
  isVisible,
  onClose,
  title: initialTitle,
  description,
  children,
  className,
  hasCancelButton,
  noBackgroundClose,
  isEditing,
  canEditTitle,
  fullScreen,
  blur,
  onTitleUpdate,
  footer,
}: ReusableModalProps) {
  const windowHeight = Dimensions.get("window").height;
  const maxModalHeight = windowHeight * 0.7;

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(initialTitle ?? "");

  useEffect(() => {
    setCurrentTitle(initialTitle ?? "");
  }, [initialTitle, isVisible]);

  const handleSaveTitle = () => {
    if (onTitleUpdate) {
      onTitleUpdate(currentTitle);
    }
    setIsEditingTitle(false);
  };

  const renderTitle = () => {
    if (isEditing && canEditTitle) {
      if (isEditingTitle) {
        return (
          <View className="flex flex-row items-center gap-x-2">
            <TextInput
              value={currentTitle}
              onChangeText={setCurrentTitle}
              className="text-gray-700 font-semibold text-lg border-b border-gray-300 flex-1"
              onBlur={handleSaveTitle}
            />
            <Pressable onPress={handleSaveTitle}>
              <Icon as={CheckIcon} className="h-5 w-5 text-primary-500" />
            </Pressable>
          </View>
        );
      } else {
        return (
          <Pressable
            className="flex flex-row items-center gap-x-2 group"
            onPress={() => setIsEditingTitle(true)}
          >
            <Text className="text-gray-700 font-semibold text-lg group-hover:text-primary-400">
              {currentTitle}
            </Text>
            <Icon
              as={EditIcon}
              className="h-4 w-4 group-hover:stroke-primary-400"
            />
          </Pressable>
        );
      }
    } else {
      return (
        <View className="flex flex-row justify-between items-center">
          <Text className="text-gray-700 font-semibold text-lg">
            {currentTitle}
          </Text>
        </View>
      );
    }
  };

  const hasFooter = !!footer;
  return (
    <Modal
      visible={isVisible}
      onRequestClose={onClose}
      transparent
      animationType="fade"
    >
      <View
        className={`p-5 flex-1 bg-black/50 items-center justify-center relative ${blur ? "backdrop-blur-md" : ""}`}
      >
        {!noBackgroundClose ? (
          <Pressable
            onPress={onClose}
            className="absolute top-0 left-0 bottom-0 right-0"
          />
        ) : null}
        <View
          className={`min-w-96 ${hasFooter ? "pt-10 pb-4" : "py-10"} rounded-xl bg-white drop-shadow-xl max-h-full ${className}`}
        >
          <ScrollView
            stickyHeaderIndices={[0]}
            style={fullScreen ? null : { maxHeight: maxModalHeight }}
            contentContainerClassName="min-h-full px-10"
          >
            <View className="pb-2 bg-white">
              {!hasCancelButton ? (
                <View className="flex flex-row mb-6 justify-between items-center">
                  <Pressable onPress={onClose}>
                    <Icon as={CloseIcon} size="md" />
                  </Pressable>
                </View>
              ) : null}
              {renderTitle()}
              {description ? (
                <Text className="text-gray-600 my-4 leading-5 text-sm">
                  {description}
                </Text>
              ) : null}
            </View>
            <View className={fullScreen ? "flex-1" : undefined}>
              {children}
            </View>
          </ScrollView>
          <PortalHost name="modal-footer">{footer}</PortalHost>
        </View>
      </View>
    </Modal>
  );
}
